import React from 'react';

import Editor from '~/components/core/Editor';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Text from '~/components/core/TextComponents/Text';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';

import { getRelevantFieldTitle } from './EmailConfigurationBuilderContainer';
import type { EmailConfigurationEditorProps } from './emailConfigurationTypes';
import EmailSignatureEditorField from './EmailSignatureEditorField';

const EditorsSkeleton = () => <SkeletonTable rowsCount={4} columnsCount={1} />;

const EmailConfigurationEditor: React.FC<EmailConfigurationEditorProps> = ({
  handleChange,
  currentEmailSubject,
  currentEmailSignature,
  suggestions,
  isSubOrg,
  isLoadingTokens,
  isErrorTokens,
}: EmailConfigurationEditorProps) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();

  return (
    // The link dialog only rejects an unsupported `{{...}}` when it has the token catalog, so an
    // errored fetch has to keep the editors gated: `useDataFetcher` leaves `data` undefined for
    // good after a failure, and an empty catalog is indistinguishable from a resolved one. Both
    // editors read the same request, so they share one gate — two would stack two indicators for
    // the one failure.
    <LoadingSwitch isLoading={isLoadingTokens} isError={isErrorTokens} LoadingComponent={EditorsSkeleton}>
      <div className="my-16">
        <Text variant={Text.VARIANTS.SM} className="my-12">
          {getRelevantFieldTitle(isSubOrg, 'Email Subject')}
        </Text>
        <Editor
          displayConfig={{ suggestions: true }}
          content={currentEmailSubject}
          onChange={(value) => handleChange(value, 'email_default_title')}
          suggestionOptions={suggestions.emailTitleTokens}
        />
      </div>
      <EmailSignatureEditorField
        organizationId={organization.id}
        label={getRelevantFieldTitle(isSubOrg, 'Signature')}
        content={currentEmailSignature}
        onChange={(value) => handleChange(value, 'email_signature_html_template')}
        suggestionOptions={suggestions.emailSignatureTokens}
        isLoadingTokens={isLoadingTokens}
      />
    </LoadingSwitch>
  );
};

export default EmailConfigurationEditor;
